import { z } from 'zod';
/**
 * **Contrats d'entrée de `CDC-1`, volet accès et mot de passe.**
 *
 * Écrits une fois ici, ils valident l'entrée de l'API et valideraient les mêmes
 * formulaires côté front sans être réécrits (`DOCTRINE.md` §4). `strictObject`
 * partout : une clé inconnue produit un 400 visible, elle ne disparaît pas en
 * silence.
 *
 * **Chaque champ porte un `.describe()`** — seul `.describe()` atteint OpenAPI,
 * donc le client généré, donc l'autocomplétion de qui écrira un front (D-024).
 * Le motif interne d'une borne reste en commentaire : il regarde qui lit le code,
 * pas qui appelle l'API.
 */
/**
 * Bornes d'une adresse email.
 *
 * `.toLowerCase()` avant tout : l'adresse est **la clé de reconnaissance d'une
 * personne au provisionnement** (Q3, Q51) et elle est unique au produit. Deux
 * casses différentes créeraient deux comptes pour la même personne, ce que Q23
 * interdit — « deux comptes ne partagent jamais une adresse ».
 */
const email = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(254)
    .refine((valeur) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valeur), {
    message: 'doit être une adresse email',
});
/**
 * Bornes d'un mot de passe **à l'entrée**.
 *
 * Douze caractères au minimum (Q72), **aucune composition imposée**. La borne
 * haute est en octets et large : elle empêche qu'un corps énorme occupe le
 * processus le temps d'être haché, elle ne borne pas une phrase de passe.
 *
 * **Ce schéma ne dit RIEN des fuites publiques.** Ce refus-là vit dans
 * `PasswordService`, côté serveur, et il doit y rester : un front qui le
 * porterait devrait embarquer la liste, donc l'exposer.
 */
const motDePasse = z
    .string()
    .min(12)
    .max(1024)
    .describe('Mot de passe. Douze caractères au minimum, aucune composition imposée. ' +
    'Les mots de passe figurant dans les fuites publiques connues sont refusés.');
/** `POST /api/v1/auth/connexion` — F-06. */
export const connexionSchema = z.strictObject({
    email: email.describe('Adresse email du compte.'),
    motDePasse: z.string().min(1).max(1024).describe('Mot de passe du compte.'),
});
/**
 * `POST /api/v1/auth/mot-de-passe-oublie` — F-07.
 *
 * Un seul champ, et c'est voulu : l'écran de connexion n'a **aucune entrée
 * dédiée pour demander un code** (Q169), donc rien d'autre à transmettre.
 */
export const motDePasseOublieSchema = z.strictObject({
    email: email.describe('Adresse email. La réponse est identique que cette adresse ait un compte ou non.'),
});
/**
 * `POST /api/v1/acces/:valeur` — F-05.
 *
 * `:valeur` porte **soit** un code de vérification à huit caractères, **soit**
 * un jeton de lien d'auto-connexion. L'adresse n'est nécessaire que dans le
 * premier cas : un code n'est unique que **par compte**, et le retrouver sans
 * savoir de qui il est exigerait de le chercher dans toute la table — ce que le
 * filet de cloisonnement refuse, et à raison. Un jeton de lien, lui, est unique
 * au produit et se suffit.
 */
export const presentationAccesSchema = z.strictObject({
    email: email
        .optional()
        .describe("Adresse email du compte. Requise quand la valeur présentée est un code à " +
        "huit caractères, inutile quand c'est un jeton de lien d'auto-connexion."),
});
/**
 * `POST /api/v1/auth/mot-de-passe/initial` — F-05.
 *
 * Le billet vient de la réponse de `POST /api/v1/acces/:valeur`. Il vaut **dix
 * minutes** et **une seule fois**.
 */
export const motDePasseInitialSchema = z.strictObject({
    billet: z
        .string()
        .min(1)
        .max(512)
        .describe('Billet rendu par POST /api/v1/acces/:valeur. Valable dix minutes, un seul usage.'),
    motDePasse,
});
/**
 * `POST /api/v1/auth/mot-de-passe` — F-07, le changement **volontaire**.
 *
 * Distinct de la réinitialisation : celle-ci passe par « mot de passe oublié »
 * puis par la route de pose. Ici la personne est connectée et prouve son mot de
 * passe courant.
 */
export const changementMotDePasseSchema = z.strictObject({
    motDePasseCourant: z
        .string()
        .min(1)
        .max(1024)
        .describe('Mot de passe actuel du compte.'),
    nouveauMotDePasse: motDePasse,
});
/**
 * `POST /api/v1/compte/adresse` — F-07.
 *
 * Deux choses sont exigées **dans cet ordre** : le mot de passe courant, puis un
 * code envoyé à la nouvelle adresse (Q492). Ce corps porte la première étape ;
 * la seconde se confirme par `POST /api/v1/acces/:valeur`.
 */
export const changementAdresseSchema = z.strictObject({
    motDePasseCourant: z
        .string()
        .min(1)
        .max(1024)
        .describe('Mot de passe actuel du compte, exigé avant tout changement.'),
    nouvelleAdresse: email.describe("Nouvelle adresse. Elle ne devient l'adresse du compte qu'une fois le code " +
        'reçu dessus saisi ; jusque-là, rien ne change.'),
});
//# sourceMappingURL=acces.contract.js.map